import { ViewConfig } from '../../../models/view-config.interface';
import { ConsigneeDeliveryNotesPaginatedService } from '@app/consignee/services/consignee-delivery-notes-paginated.service';
import { AppStore } from '@app/stores/appStore';
import { ConsigneeViewActionsService } from '../consignee-view-actions.service';
import { TableActionsConfig } from '@app/shared/components/table-cell-components/table-actions.component';
import { ColumnCreator } from '../column-creator';
import { FilterKeyCreator } from '../filter-key-creator';
import {
    DeliveryNoteData,
    TableRowData,
} from '@app/shared/models/table-data-types';

export interface ConsigneeDeliveryNotesConfigDeps {
    consigneeDeliveryNotesPaginatedService: ConsigneeDeliveryNotesPaginatedService;
    actionsService: ConsigneeViewActionsService;
    appStore: InstanceType<typeof AppStore>;
}

export class ConsigneeDeliveryNotesConfig {
    static create(deps: ConsigneeDeliveryNotesConfigDeps): ViewConfig {
        return {
            id: 'consignee-deliverynotes',
            name: 'Delivery Notes',
            translationKey: 'OUTGOING-GOODS.TOGGLER.DELIVERY-NOTES',
            type: 'deliverynotes',
            userRole: 'consignee',
            apiConfig: {
                loadData: () => {
                    const orgSiteKey =
                        deps.appStore.currentOrganizationSiteKey();
                    if (!orgSiteKey) {
                        return Promise.resolve();
                    }
                    return deps.consigneeDeliveryNotesPaginatedService.loadConsigneeDeliveryNotesPaginated(
                        orgSiteKey
                    );
                },
            },
            tableConfig: {
                columns: [
                    ColumnCreator.select({ visible: false }),
                    ColumnCreator.actions({
                        width: '200px',
                        configuration: {
                            type: 'deliveryNote',
                            showView: true,
                            showDownload: true,
                            showAddNotes: true,

                            // Endpoints
                            viewEndpoint: (item: TableRowData) =>
                                deps.actionsService.viewDeliveryNote(
                                    item as DeliveryNoteData
                                ),
                            downloadEndpoint: (item: TableRowData) =>
                                deps.actionsService.downloadDeliveryNote(
                                    item as DeliveryNoteData
                                ),
                            addNotesEndpoint: (item: TableRowData) =>
                                deps.actionsService.addFineDiscrepancy(
                                    item as DeliveryNoteData
                                ),

                            // Configuration
                            organizationSiteKey:
                                deps.appStore.currentOrganizationSiteKey(),
                            context: 'consignee',

                            // Visibility conditions
                            isEditVisible: () => false,
                            isDeleteVisible: () => false,
                            isAddNotesVisible: (item: TableRowData) =>
                                (item as DeliveryNoteData).checkinDate &&
                                !(item as DeliveryNoteData).fineDiscrepancies,
                        } as TableActionsConfig,
                    }),
                    ColumnCreator.deliveryNoteNumber(),
                    ColumnCreator.referenceNumber({
                        orgType: 'consignee',
                        readOnly: true,
                        organizationSiteKeyResolver: row =>
                            (row as DeliveryNoteData).consigneeKey || '',
                    }),
                    ColumnCreator.orderNumber(),
                    // Sorting disabled for 2.4.3 - the backend sortBy contract does
                    // not accept this field yet. Re-enable once it does.
                    ColumnCreator.consigneeOrderNumber({ sortable: false }),
                    ColumnCreator.deliveryNoteBundleNumber(),
                    ColumnCreator.tourNumber(),
                    // AI generated start (model: Opus 5)
                    // the value comes from DeliveryNoteInPoolConsigneeV3, which the
                    // consignee endpoint returns; the generic table reads it untyped
                    ColumnCreator.externalTourName(),
                    // AI generated end
                    ColumnCreator.originalConsignor(),
                    ColumnCreator.originalConsignorGln(),
                    ColumnCreator.consignorName(),
                    ColumnCreator.carrierName({
                        header: 'INCOMING-GOODS.TABLE.CARRIER-NAME',
                    }),
                    ColumnCreator.dateShipped(),
                    ColumnCreator.dateAnnounced(),
                    ColumnCreator.dateReceived(),
                    ColumnCreator.carrierLicensePlate({
                        plateType: 'incoming',
                    }),
                    ColumnCreator.dnStatus(),
                    ColumnCreator.discrepancies(),
                    // Sorting disabled for 2.4.3 - see consigneeOrderNumber above.
                    ColumnCreator.controls({ sortable: false }),
                    ColumnCreator.palletTickets(),
                    ColumnCreator.qsStatus({
                        header: 'INCOMING-GOODS.TABLE.QS_STATUS',
                        sortable: false,
                    }),
                ],
                features: {
                    sortable: true,
                    filterable: true,
                    paginated: true,
                    selectable: false,
                    expandable: false,
                },
            },
            filterConfig: {
                filterKeys: [
                    FilterKeyCreator.text(
                        'number',
                        'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DELIVERY-NOTE-NAME'
                    ),
                    FilterKeyCreator.text(
                        'referenceNumber',
                        'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.REFERENCE-NUMBER',
                        'properties.referenceNumber'
                    ),
                    FilterKeyCreator.text(
                        'orderNumber',
                        'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ORDER-NO'
                    ),
                    FilterKeyCreator.text(
                        'deliveryNoteBundleNumber',
                        'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.DELIVERY-NO'
                    ),
                    FilterKeyCreator.text(
                        'tourNumber',
                        'SPEDITEUR.TABLE.HEADERS.TOUR-KEY'
                    ),
                    // AI generated start (model: Opus 5)
                    FilterKeyCreator.text(
                        'externalTourName',
                        'SPEDITEUR.TABLE.HEADERS.TOUR-NAME'
                    ),
                    // AI generated end
                    FilterKeyCreator.text(
                        'originalConsignor',
                        'GLOBAL.ORIGINAL_CONSIGNOR'
                    ),
                    FilterKeyCreator.text(
                        'originalConsignorGln',
                        'GLOBAL.GLN'
                    ),
                    FilterKeyCreator.text(
                        'consignorName',
                        'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CONSIGNOR-NAME'
                    ),
                    FilterKeyCreator.text(
                        'carrierName',
                        'INCOMING-GOODS.TABLE.CARRIER-NAME'
                    ),
                    FilterKeyCreator.date(
                        'checkoutDate',
                        'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.SHIPPING-DATE'
                    ),
                    FilterKeyCreator.date(
                        'dateAnnounced',
                        'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.ADVISED-DATE'
                    ),
                    FilterKeyCreator.date(
                        'checkinDate',
                        'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.RECEIVE-DATE'
                    ),
                    FilterKeyCreator.text(
                        'carrierNumberPlateIncoming',
                        'SPEDITEUR.TABLE.HEADERS.PLATE-OUT'
                    ),
                    FilterKeyCreator.status(
                        'status',
                        'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DELIVERY-NOTE-STATUS'
                    ),
                    FilterKeyCreator.boolean(
                        'discrepancies',
                        'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DISCREPANCIES'
                    ),
                    FilterKeyCreator.boolean(
                        'palletTickets',
                        'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.PALLET-TICKET'
                    ),
                ],
                showClosed: true,
                enableAdvancedFilters: true,
            },
            searchConfig: {
                // 'consigneeOrderNumber' and 'controls' are absent on purpose -
                // the backend sortBy contract does not accept them, so their
                // columns are declared `sortable: false` above. Re-add together
                // with the matching column/filter entries once it does.
                validSortFields: [
                    'dateCreated',
                    'number',
                    'orderNumber',
                    'consignorName',
                    'consigneeName',
                    'carrierName',
                    'deliveryNoteBundleNumber',
                    'dateShipped',
                    'dateAnnounced',
                    'dateReceived',
                    'carrierNumberPlateIncoming',
                    // AI generated start (model: Opus 5)
                    'externalTourName',
                    // AI generated end
                    'status',
                ],
                defaultSortField: 'dateCreated',
                defaultSortDirection: 'desc',
            },
            permissions: {
                canCreate: false,
                canUpdate: true,
                canDelete: false,
                canExport: true,
                canImport: false,
            },
            selectionConfig: {
                clearOnViewTypeSwitch: true,
            },
            headerActionConfig: {
                headerActions: [
                    {
                        id: 'scanWallet',
                        label: 'Scan Wallet',
                        translationKey: 'INCOMING-GOODS.BUTTONS.SCAN-WALLET',
                        icon: 'qr_code_scanner',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 2xl:!tw-text-xl',
                        position: 'right',
                        order: 2,
                        visible: (item, selection) => true,
                        // Der Wallet-Scan startet einen Check-in. Fernzugriff
                        // ist nur lesend — das Backend vergibt der Site dann
                        // keine consignee_checkins_create-Berechtigung.
                        disabled: (item, selection) =>
                            !deps.appStore.currentSiteRole()?.siteInfo
                                ?.permissions?.consignee_checkins_create,
                        handler: async () => {
                            deps.actionsService.scanWallet();
                        },
                        tooltip: 'INCOMING-GOODS.BUTTONS.SCAN-WALLET',
                    },
                ],
            },
        };
    }
}
